import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { CalendarClock, TrendingUp } from "lucide-react";
import { useCurrency } from "@/contexts/CurrencyContext";

const TOKENS = [
  { symbol: "BTC", price: 65000 },
  { symbol: "ETH", price: 3200 },
  { symbol: "SOL", price: 150 },
  { symbol: "BNB", price: 580 },
];

const DCASimulator = () => {
  const [token, setToken] = useState(0);
  const [monthly, setMonthly] = useState("100");
  const [months, setMonths] = useState(12);
  const { format } = useCurrency();

  const results = useMemo(() => {
    const amt = parseFloat(monthly) || 0;
    const t = TOKENS[token];
    const totalInvested = amt * months;

    // Simulate with price variance
    let totalCoins = 0;
    const prices: number[] = [];
    for (let i = 0; i < months; i++) {
      const variance = 1 + (Math.sin(i * 0.8) * 0.15 + Math.cos(i * 1.3) * 0.1);
      const price = t.price * variance;
      prices.push(price);
      totalCoins += amt / price;
    }

    const avgPrice = totalInvested / totalCoins;
    const currentValue = totalCoins * t.price;
    const pnl = currentValue - totalInvested;
    const roi = totalInvested > 0 ? (pnl / totalInvested) * 100 : 0;

    return { totalInvested, totalCoins, avgPrice, currentValue, pnl, roi, prices };
  }, [token, monthly, months]);

  // Mini chart
  const chartH = 40;
  const chartW = 180;
  const minP = Math.min(...results.prices, 1);
  const maxP = Math.max(...results.prices, 1);
  const rangeP = maxP - minP || 1;
  const pathD = results.prices
    .map((p, i) => `${i === 0 ? "M" : "L"} ${(i / (results.prices.length - 1)) * chartW} ${chartH - ((p - minP) / rangeP) * (chartH - 4) - 2}`)
    .join(" ");

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-border bg-card p-5 shadow-card space-y-4"
    >
      <div className="flex items-center gap-2">
        <CalendarClock className="w-5 h-5 text-primary" />
        <h3 className="font-semibold text-foreground text-sm">DCA Simulator</h3>
      </div>

      <div className="flex gap-1.5">
        {TOKENS.map((t, i) => (
          <button
            key={t.symbol}
            onClick={() => setToken(i)}
            className={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all border ${
              i === token ? "border-primary/40 bg-primary/10 text-primary" : "border-border/30 text-muted-foreground hover:border-primary/20"
            }`}
          >
            {t.symbol}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Monthly ($)</label>
          <input
            type="number"
            value={monthly}
            onChange={(e) => setMonthly(e.target.value)}
            min={1}
            className="w-full mt-1 px-3 py-2 rounded-lg bg-secondary/50 border border-border/40 text-sm font-mono text-foreground focus:outline-none focus:border-primary/40"
          />
        </div>
        <div>
          <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Duration</label>
          <select
            value={months}
            onChange={(e) => setMonths(Number(e.target.value))}
            className="w-full mt-1 px-3 py-2 rounded-lg bg-secondary/50 border border-border/40 text-sm text-foreground focus:outline-none focus:border-primary/40"
          >
            {[6, 12, 24, 36, 48, 60].map((m) => (
              <option key={m} value={m}>{m < 12 ? `${m}mo` : `${m / 12}yr`}</option>
            ))}
          </select>
        </div>
      </div>

      <svg width={chartW} height={chartH} className="w-full">
        <path d={pathD} fill="none" stroke="hsl(var(--primary))" strokeWidth="1.5" strokeLinecap="round" />
      </svg>

      <div className="grid grid-cols-2 gap-x-4 gap-y-2 pt-2 border-t border-border/30 text-xs">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Invested</span>
          <span className="font-mono text-foreground">{format(results.totalInvested)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Value</span>
          <span className="font-mono text-foreground">{format(results.currentValue)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Avg Price</span>
          <span className="font-mono text-foreground">${results.avgPrice.toFixed(2)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">P&L</span>
          <span className={`font-mono font-bold ${results.pnl >= 0 ? "text-[hsl(var(--success))]" : "text-destructive"}`}>
            {results.pnl >= 0 ? "+" : ""}{format(results.pnl)} ({results.roi.toFixed(1)}%)
          </span>
        </div>
      </div>
    </motion.div>
  );
};

export default DCASimulator;
